import React, { useMemo, useCallback } from 'react';
import { Card, Text } from '@mantine/core';
import { MetricConfig, MetricData } from '../../types/metrics';
import { usePreviousPeriodData } from '../../hooks/usePreviousPeriodData';
import { useStatCardsBatchContext } from '../../context/StatCardsBatchContext';
import { getMetricPathFromEndpoint } from '../../utils/metricPath';
import { BmBadge } from '../ui/BmBadge';
import { buildPeriodComparison } from '../../utils/deltaSentiment';
import { formatMetricValue } from '../../utils/metricValueFormat';
import { isSnapshotStatCard } from '../../utils/statCardDisplay';
import { StatCardMetricIcon } from '../ui/metricIcons';
import './StatCard.css';

interface StatCardProps {
  config: MetricConfig;
  data: MetricData | null;
  loading: boolean;
  error?: string | null;
  storeId?: string | null;
  startDate?: string;
  endDate?: string;
}

const StatCard: React.FC<StatCardProps> = ({ 
  config, 
  data, 
  loading, 
  error,
  storeId,
  startDate,
  endDate
}) => {
  const batchContext = useStatCardsBatchContext();
  const metricPath = getMetricPathFromEndpoint(config.apiEndpoint);
  const batchEntry = batchContext.isError ? undefined : batchContext.data?.[metricPath];
  const useBatchPrevious = batchEntry?.previous !== undefined;

  const { 
    data: previousData, 
    isLoading: previousLoading, 
    error: previousError 
  } = usePreviousPeriodData({
    config,
    storeId: storeId || null,
    startDate: startDate || '',
    endDate: endDate || '',
    enabled: !useBatchPrevious && !batchContext.isLoading && !isSnapshotStatCard(config.id),
  });

  const effectivePreviousData = batchEntry?.previous ?? previousData;

  console.log(`🔍 StatCard ${config.id} - Current data:`, data);
  console.log(`🔍 StatCard ${config.id} - Previous data:`, effectivePreviousData);
  console.log(`🔍 StatCard ${config.id} - Loading:`, loading);
  console.log(`🔍 StatCard ${config.id} - Previous loading:`, previousLoading);

  // Memoizar cálculo del valor actual
  const currentValue = useMemo(() => {
    if (!data) {
      console.log(`❌ StatCard ${config.id} - No current data available`);
      return 0;
    }
    let value = data.value || 0;
    
    // Validación específica para la tasa de CyD
    if (config.id === 'cyd-rate') {
      // Asegurar que el valor sea un número válido
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      if (isNaN(numValue) || !isFinite(numValue)) {
        console.warn(`⚠️ Valor inválido en cyd-rate:`, value);
        value = 0;
      } else {
        value = Math.round(numValue * 100) / 100; // Redondear a 2 decimales
      }
    }
    
    console.log(`📊 StatCard ${config.id} - Current value:`, value);
    return value;
  }, [data, config.id]);

  // Memoizar cálculo del valor previo
  const previousValue = useMemo(() => {
    if (!effectivePreviousData) {
      console.log(`❌ StatCard ${config.id} - No previous data available`);
      return 0;
    }
    
    // Validación específica para diferentes tipos de datos previos
    let previous = 0;
    
    if (typeof effectivePreviousData === 'object' && effectivePreviousData !== null) {
      if ((effectivePreviousData as any).value !== undefined) {
        previous = (effectivePreviousData as any).value;
      } else if (Array.isArray(effectivePreviousData)) {
        previous = effectivePreviousData.reduce((sum, item) => {
          const itemValue = (item as any).value || 0;
          return sum + (isNaN(itemValue) ? 0 : itemValue);
        }, 0);
      }
    } else if (typeof effectivePreviousData === 'number') {
      previous = effectivePreviousData;
    }
    
    // Validación final para asegurar que sea un número válido
    if (isNaN(previous) || !isFinite(previous)) {
      console.warn(`⚠️ Valor previo inválido en ${config.id}:`, previous);
      previous = 0;
    }
    
    console.log(`📊 StatCard ${config.id} - Previous value:`, previous);
    return previous;
  }, [effectivePreviousData, config.id]);

  // Memoizar función de formateo
  const formatValue = useCallback(
    (value: number) => formatMetricValue(config.id, value),
    [config.id]
  );

  const periodComparison = useMemo(
    () => buildPeriodComparison(config.id, currentValue, previousValue, startDate, endDate),
    [config.id, currentValue, previousValue, startDate, endDate]
  );

  const icon = useMemo(
    () => <StatCardMetricIcon metricId={config.id} />,
    [config.id]
  );

  const isLoadingState = loading || (useBatchPrevious ? false : previousLoading);

  const renderHeader = () => (
    <div className="stat-card__header">
      <div className="stat-card__icon-box" aria-hidden>
        {icon}
      </div>
      <span className="stat-card__title">{config.title}</span>
    </div>
  );

  const renderDeltaBadge = () => {
    if (!periodComparison.chipLabel || !periodComparison.chipVariant) {
      return null;
    }
    return (
      <BmBadge variant={periodComparison.chipVariant} size="lg">
        {periodComparison.chipLabel}
      </BmBadge>
    );
  };

  if (isLoadingState) {
    return (
      <Card className="stat-card" shadow="sm" padding="lg" radius="md" withBorder>
        {renderHeader()}
        <Text>Cargando...</Text>
      </Card>
    );
  }

  if (error || previousError) {
    return (
      <Card className="stat-card" shadow="sm" padding="lg" radius="md" withBorder>
        {renderHeader()}
        <Text c="negative">Error: {error || previousError?.message}</Text>
      </Card>
    );
  }

  console.log(`📈 ${config.title} - Comparison:`, periodComparison);

  return (
    <Card className="stat-card" shadow="sm" padding="lg" radius="md" withBorder>
      {renderHeader()}

      <div className="stat-card__body">
        <div className="stat-card__value-row">
          <span className="stat-card__value">{formatValue(currentValue)}</span>
          {renderDeltaBadge()}
        </div>
      </div>

      <p className="stat-card__footnote">{periodComparison.footnote}</p>
    </Card>
  );
};

// Memoizar componente para evitar re-renders innecesarios
export default React.memo(StatCard, (prevProps, nextProps) => {
  // Comparar props relevantes
  return (
    prevProps.config.id === nextProps.config.id &&
    prevProps.loading === nextProps.loading &&
    prevProps.error === nextProps.error &&
    prevProps.storeId === nextProps.storeId &&
    prevProps.startDate === nextProps.startDate &&
    prevProps.endDate === nextProps.endDate &&
    // Comparar datos: si ambos son null/undefined, son iguales
    // Si uno es null y el otro no, son diferentes
    // Si ambos tienen datos, comparar el valor
    (prevProps.data === nextProps.data || 
     (prevProps.data?.value === nextProps.data?.value))
  );
});
